import axios, { isAxiosError } from 'axios'

export type FileUploadInput = {
  /** The bytes. A File survives a postMessage clone with its name intact, so a caller usually has one. */
  content: Blob
  filename: string
  /**
   * Metadata graph the file's triples are written to. The servlet defaults it
   * when absent, and checks write permission on whichever graph it ends up
   * using -- so this is a target, not a boundary.
   */
  graph?: string
  /** Subject the file belongs to, written as uploadanyfile:belongsTo. */
  belongsTo?: string
  /** Aborts the request, e.g. when the user cancels or the component unmounts. */
  signal?: AbortSignal
}

/** The URI of the UploadedFile resource the servlet created, and the graph its triples went to. */
export type FileUploadResult = {
  fileUri: string
  graph?: string
}

export type RequestFileUpload = (input: FileUploadInput) => Promise<FileUploadResult>

type UploadAnyFileResponse = {
  success?: boolean
  fileuri?: string
  /** What SparqlBeeUtils.senErrorMessage sends, which is the refusal path. */
  errorMessage?: string
  /** What a few hand-written branches in UploadServlet send instead. */
  error?: string
}

/**
 * Upload a file through the legacy multipart upload servlet, as the
 * `uploadAnyFile` command.
 *
 * Curried on the URL, matching request-data-update and request-rdf-upload: the
 * URL is derived once (see use-app-ctx) while the payload differs per call site.
 *
 * The servlet stores the bytes under the repo's file directory and writes
 * `uploadanyfile:` metadata triples describing them, answering
 * `{"success": true, "fileuri": "..."}`. It refuses with 409 when the caller may
 * not upload, or may not write the metadata graph.
 */
export function requestFileUpload(uploadUrl: string): RequestFileUpload {
  return async function requestFileUpload(input: FileUploadInput): Promise<FileUploadResult> {
    const form = new FormData()
    form.append('command', 'uploadAnyFile')
    form.append('file', input.content, input.filename)
    if (input.graph) form.append('graph', input.graph)
    if (input.belongsTo) form.append('belongsTo', input.belongsTo)

    const response = await axios
      .post<UploadAnyFileResponse>(uploadUrl, form, {
        withCredentials: true,
        signal: input.signal,
      })
      .catch((error: unknown) => { throw uploadError(uploadUrl, error) })

    /*
     * The servlet answers 200 with `{"success": true}` and no fileuri when the
     * metadata insert failed, so a caller that trusted the status alone would
     * report a successful upload of a file nothing can reference.
     */
    const fileUri = response.data?.fileuri
    if (!fileUri) {
      throw new Error(
        'Upload: the server stored the file but returned no fileuri, so its metadata was not written ' +
        'and nothing can reference it. ' + (response.data?.errorMessage ?? response.data?.error ?? '')
      )
    }

    return { fileUri, graph: input.graph }
  }
}

/**
 * Surface the servlet's own refusal instead of "Request failed with status code 409".
 *
 * Two response shapes, because the servlet has two: senErrorMessage sends
 * `errorMessage`, and a few hand-written branches in UploadServlet send `error`.
 */
function uploadError(uploadUrl: string, error: unknown): Error {
  if (!isAxiosError(error)) return error instanceof Error ? error : new Error(String(error))

  const body = error.response?.data as UploadAnyFileResponse | undefined
  const fromServer = body?.errorMessage ?? body?.error
  if (fromServer) return new Error(String(fromServer).trim())

  if (!error.response) {
    /*
     * A rejected request says only "Network Error"; the reason the browser
     * actually had -- a net::ERR_ code, a CORS refusal, a forced HTTPS upgrade
     * -- reaches the console and nowhere else. One local HSTS entry for
     * localhost cost six rounds of debugging for want of this line.
     */
    console.error('[uploadFile] POST to', uploadUrl, 'was rejected by the browser:', error)

    /*
     * No response reached us, which does NOT mean the upload did not happen: a
     * connection dropped after the body was sent is indistinguishable here from
     * one that never arrived, and the servlet may already have stored the file.
     * Saying "failed" would invite a retry that silently stores it twice.
     */
    return new Error(
      `Upload: no response from ${uploadUrl} (${error.message}). The file may already have been ` +
      'stored - check before retrying, because a retry would store it again. ' +
      'The browser logged the underlying reason to the console.'
    )
  }

  return error
}
